import React, { ReactNode } from "react";
import styled, { css } from "styled-components";

import { VSpace, colorFromTheme } from "@examspark/common-ui/server";
import { ParsedUnitObjective, Unit } from "@examspark/types";

import { getObjectiveCategory, getObjectiveCurriculumUsage } from "..";

import ObjectiveBadge from "./ObjectiveBadge";
import ObjectiveCategoryBadge from "./ObjectiveCategoryBadge";
import ObjectiveCurriculumUsageBadge from "./ObjectiveCurriculumUsageBadge";
import PrintUnitContent from "./PrintUnitContent";

const StyledTable = styled.table`
  border-collapse: collapse;

  /* Using this with specificity causes strange layout issues with specificity that is taller than a single page. */
  body.no-specificity & tbody {
    break-inside: avoid;
  }

  & tbody tr {
    border-top: 1px solid #ddd;
    border-bottom: 1px solid #ddd;
  }

  body.show-specificity & {
    width: 100%;
  }
  body.show-specificity & tr td:first-child {
    width: 25%;
  }
`;

const StyledTableHead = styled.thead`
  body.show-specificity & > tr > th:nth-child(1) {
    width: 25%;
  }

  th {
    text-align: left;
    padding: 1rem;
    color: #333;
    font-size: 1.3rem;
  }
  th:first-child {
    padding-left: 0;
  }
`;

const StyledObjectiveRow = styled.tr<{ isParent?: boolean }>`
  td {
    padding-top: 0.8rem;
    padding-bottom: 0.8rem;
    padding-left: 0.8rem;
  }
  td:first-child {
    padding-right: 1rem;
  }
  td:last-child {
    padding-right: 0;
  }

  ${(p) =>
    p.isParent &&
    css`
      background-color: ${colorFromTheme("surface2")};
    `}
`;

const RunningHeader = styled.header.attrs({
  className: "running left",
})`
  width: 10in;
`;

const StyledStandardCell = styled.td`
  line-height: 1.5em;
  font-size: 1.3rem;
  vertical-align: top;
`;

const StyledObjectiveType = styled.div`
  color: ${colorFromTheme("primary")};
`;

type Props = {
  flattenedObjectives: ParsedUnitObjective[] | null | undefined;
  missingParentIds: Set<string>;
  unit?: Unit | null;
  toolbar?: ReactNode;
  showSpecificity?: boolean;
  showAnnotationCcrs?: boolean;
  showAnnotationRcfp?: boolean;
  renderObjectiveDetails?: (
    objective: ParsedUnitObjective,
    isInjected: boolean,
  ) => ReactNode;
};

const PrintObjectives = ({
  flattenedObjectives,
  missingParentIds,
  unit,
  ...props
}: Props) => {
  if (!flattenedObjectives || flattenedObjectives.length === 0) {
    return <></>;
  }

  const filteredObjectives = flattenedObjectives;

  return (
    <div>
      <VSpace size={"2rem"}>
        <div>
          {filteredObjectives?.length !== 0 && (
            <>
              <RunningHeader>
                <StyledTable>
                  <StyledTableHead>
                    <tr>
                      <th>Standard</th>
                    </tr>
                  </StyledTableHead>
                </StyledTable>
              </RunningHeader>
              <StyledTable>
                <StyledTableHead>
                  <tr>
                    <th>Standard</th>
                  </tr>
                </StyledTableHead>
                {flattenedObjectives.map((objective) => {
                  const isAddedParent = missingParentIds.has(objective.id);
                  return (
                    <tbody key={objective.id}>
                      <StyledObjectiveRow isParent={isAddedParent}>
                        <StyledStandardCell>
                          <span>
                            {/* // Link this in the PDF? */}
                            <ObjectiveBadge objective={objective} />
                          </span>
                          <PrintUnitContent
                            content={
                              objective.objectivesItem?.data?.content ??
                              objective.content
                            }
                          />
                          <StyledObjectiveType>
                            <ObjectiveCategoryBadge
                              value={getObjectiveCategory(objective)}
                              showLabel
                            />
                            <ObjectiveCurriculumUsageBadge
                              value={getObjectiveCurriculumUsage(
                                objective.objectivesItem,
                              )}
                              showLabel
                            />
                          </StyledObjectiveType>
                          {props.renderObjectiveDetails?.(objective, false)}
                        </StyledStandardCell>
                      </StyledObjectiveRow>
                    </tbody>
                  );
                })}
              </StyledTable>
            </>
          )}
        </div>
      </VSpace>
    </div>
  );
};

export default PrintObjectives;
